import React, { useState, useEffect, ReactElement } from 'react';
import { useSearchParams, useParams, Params } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { Attribute, Programme } from '../../../types/entity.types';
import { Field } from '../../../types/common.types';
import { RootState } from '../../../types/state/root.types';
import { RegistrationState } from '../../../types/state/registration.types';

import { ERROR, REQUIRED_REGISTRATION_FIELDS } from '../../../utils/constants';

import {
  requestFindActiveProgrammes,
  requestCreateMember
} from '../../../api/external-registration/external-registration.actions';
import { addSystemNotice, addSpinner } from '../../../slices/system/system.slice';

import ProgrammeDetails from '../../../components/screens/auth/programme-details/ProgrammeDetails';

type PayFastField = {
  name: string;
  value: string;
};

const ProgrammeDetailsContainer = (): ReactElement => {
  const { programmes } = useSelector((state: RootState): RegistrationState => state.registration);

  const [programme, setProgramme] = useState<Programme | undefined>(undefined);
  const [fields, setFields] = useState<Field[]>([]);

  const { externalReference }: Params = useParams();

  // find type later
  const [searchParams] = useSearchParams();

  const dispatch: Dispatch = useDispatch();

  useEffect((): void => {
    if (!programmes || programmes.length === 0) {
      dispatch(requestFindActiveProgrammes());
    }
  }, [dispatch, programmes]);

  useEffect((): void => {
    if (programmes && programmes.length > 0) {
      const foundProgramme: Programme | undefined = programmes.find(
        (programme: Programme): boolean => programme.externalReference === externalReference
      );
      setProgramme(foundProgramme);
    }
  }, [programmes, externalReference]);

  useEffect((): void => {
    if (programme) {
      const fields: Field[] = (programme.registrationRequirement?.requiredRegistrationFields as string[]).map(
        (field: string): Field => {
          const attributes: string[] = field.split('**');
          return {
            label: attributes[0] as string,
            optional: attributes[1] === 'optional',
            type: attributes[2] as string,
            value: ''
          };
        }
      );
      setFields([...REQUIRED_REGISTRATION_FIELDS, ...fields]);
    }
  }, [programme]);

  const updateField = (label: string, value: string | boolean): void => {
    const copyOfFields: Field[] = structuredClone(fields);

    const field: Field | undefined = copyOfFields.find((field: Field): boolean => field.label === label);
    if (field) {
      field.value = value;

      setFields(copyOfFields);
    }
  };

  const showToaster = (message: string): void => {
    dispatch(addSystemNotice({ type: ERROR, message }));
  };

  const getValue = (label: string): string | boolean | undefined =>
    fields.find((field: Field): boolean => field.label === label)?.value;

  const getItemDescription = (): string => {
    const memberNumber: string | boolean | undefined = getValue('Member Number');
    const emailAddress: string | boolean | undefined = getValue('Email Address');

    let description: string = '';
    if (memberNumber) {
      description = memberNumber as string;
    } else if (emailAddress) {
      description = emailAddress as string;
    }

    const idNumber: string = getValue('ID Number') as string;
    if (idNumber) {
      description = `${description} / ${idNumber}`;
    }

    return description;
  };

  const postToPayfast = (memberExternalReference: string): void => {
    // I need to look at the signature generation at some point
    // so I am commenting out the signature generation code for now

    if (!memberExternalReference) {
      showToaster('An error occurred. Please try again.');
      return;
    }

    dispatch(addSpinner('Navigating to Payfast'));
    const form: HTMLFormElement = document.createElement('form');
    const payfastURL: string | undefined = process.env.REACT_APP_PAYFAST_URL;

    if (!payfastURL) {
      showToaster('An error occurred');
      throw new Error('PayFast URL is undefined');
    }

    form.method = 'post';
    form.action = payfastURL;

    let returnUrl: string = `${process.env.REACT_APP_HOST}/register/success/${externalReference}/${memberExternalReference}`;
    let cancelUrl: string = `${process.env.REACT_APP_HOST}/register/${externalReference}`;
    const theme: string | null = searchParams.get('theme');
    if (theme) {
      returnUrl = `${returnUrl}?theme=${theme}`;
      cancelUrl = `${cancelUrl}?theme=${theme}`;
    }

    const fields: PayFastField[] = [
      { name: 'merchant_id', value: programme?.registrationRequirement?.payfastMerchantId as string },
      { name: 'merchant_key', value: programme?.registrationRequirement?.payfastMerchantKey as string },
      { name: 'return_url', value: returnUrl },
      { name: 'cancel_url', value: cancelUrl },
      // { name: 'notify_url', value: `${process.env.REACT_APP_API_ENDPOINT}/payfast` },
      { name: 'amount', value: programme?.registrationRequirement?.programmePrice as string },
      { name: 'item_name', value: programme?.name as string },
      {
        name: 'item_description',
        value: getItemDescription()
      },
      { name: 'custom_str1', value: programme?.registrationRequirement?.programmePrice as string }
    ];

    // let parameterString = '';
    fields.forEach((field: PayFastField): void => {
      const input: HTMLInputElement = document.createElement('input');
      input.type = 'hidden';
      input.name = field.name;
      input.value = field.value;
      form.appendChild(input);
      // parameterString = `${parameterString}${field.name}=${encodeURIComponent(field.value.trim()).replace(
      //   /%20/g,
      //   '+'
      // )}&`;
    });

    // parameterString = `${parameterString}passphrase=${encodeURIComponent('Vetromediasouthafrica201').replace(
    //   /%20/g,
    //   '+'
    // )}`;

    // const input = document.createElement('input');
    // input.type = 'hidden';
    // input.name = 'signature';
    // input.value = md5(parameterString);
    // form.appendChild(input);

    document.body.appendChild(form);
    form.submit();
  };

  const handleProceedToPaymentClick = (): void => {
    const attributes: Attribute[] = [];
    fields.forEach(({ label, value }: Field): void => {
      if (value && !REQUIRED_REGISTRATION_FIELDS.find((field: Field): boolean => field.label === label)) {
        attributes.push({
          key: label,
          value: value.toString()
        });
      }
    });

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to external registration actions
      requestCreateMember({
        input: {
          programmeExternalReference: externalReference,
          member: {
            emailAddress: getValue('Email Address'),
            phoneNumber: getValue('Mobile Number'),
            firstName: getValue('First Name'),
            lastName: getValue('Last Name'),
            paid: false,
            attributes
          }
        },
        onSuccess: (data: { memberExternalReference: string }): void => postToPayfast(data?.memberExternalReference)
      })
    );
  };

  return (
    <ProgrammeDetails
      programme={programme}
      fields={fields}
      updateField={updateField}
      showToaster={showToaster}
      onProceedToPaymentClick={handleProceedToPaymentClick}
    />
  );
};

export default ProgrammeDetailsContainer;
